import { useCallback } from "react";

/**
 * Creates handlers for vertical thumb mouse down events
 * @param {React.RefObject} scrollAreaRef - Reference to scroll area
 * @param {Function} setDragState - State setter for drag state
 * @returns {Function} - Mouse down handler for vertical thumb
 */
export const useVerticalThumbMouseDown = (scrollAreaRef, setDragState) => {
  return useCallback(
    (e) => {
      e.preventDefault();
      const el = scrollAreaRef.current;
      if (!el) return;

      setDragState({
        axis: "v",
        startClient: e.clientY,
        startScroll: el.scrollTop,
      });
    },
    [scrollAreaRef, setDragState]
  );
};

/**
 * Creates handlers for horizontal thumb mouse down events
 * @param {React.RefObject} scrollAreaRef - Reference to scroll area
 * @param {Function} setDragState - State setter for drag state
 * @returns {Function} - Mouse down handler for horizontal thumb
 */
export const useHorizontalThumbMouseDown = (scrollAreaRef, setDragState) => {
  return useCallback(
    (e) => {
      e.preventDefault();
      const el = scrollAreaRef.current;
      if (!el) return;

      setDragState({
        axis: "h",
        startClient: e.clientX,
        startScroll: el.scrollLeft,
      });
    },
    [scrollAreaRef, setDragState]
  );
};

/**
 * Creates handlers for vertical track mouse down events
 * @param {React.RefObject} vTrackRef - Reference to vertical track
 * @param {React.RefObject} scrollAreaRef - Reference to scroll area
 * @returns {Function} - Mouse down handler for vertical track
 */
export const useVerticalTrackMouseDown = (vTrackRef, scrollAreaRef) => {
  return useCallback(
    (e) => {
      // Click on track to jump toward that position
      if (vTrackRef.current && e.target === vTrackRef.current) {
        const el = scrollAreaRef.current;
        if (!el) return;

        const rect = vTrackRef.current.getBoundingClientRect();
        const contentScrollable = Math.max(
          0,
          el.scrollHeight - el.clientHeight
        );
        if (contentScrollable <= 0) return;

        // Simple proportional click with smooth scrolling
        const clickY = e.clientY - rect.top;
        const scrollRatio = Math.max(0, Math.min(1, clickY / rect.height));
        const newScrollTop = scrollRatio * contentScrollable;

        el.scrollTo({
          top: newScrollTop,
          behavior: "smooth",
        });
      }
    },
    [vTrackRef, scrollAreaRef]
  );
};

/**
 * Creates handlers for horizontal track mouse down events
 * @param {React.RefObject} hTrackRef - Reference to horizontal track
 * @param {React.RefObject} scrollAreaRef - Reference to scroll area
 * @returns {Function} - Mouse down handler for horizontal track
 */
export const useHorizontalTrackMouseDown = (hTrackRef, scrollAreaRef) => {
  return useCallback(
    (e) => {
      if (hTrackRef.current && e.target === hTrackRef.current) {
        const el = scrollAreaRef.current;
        if (!el) return;

        const rect = hTrackRef.current.getBoundingClientRect();
        const contentScrollable = Math.max(0, el.scrollWidth - el.clientWidth);
        if (contentScrollable <= 0) return;

        // Simple proportional click with smooth scrolling
        const clickX = e.clientX - rect.left;
        const scrollRatio = Math.max(0, Math.min(1, clickX / rect.width));
        const newScrollLeft = scrollRatio * contentScrollable;

        el.scrollTo({
          left: newScrollLeft,
          behavior: "smooth",
        });
      }
    },
    [hTrackRef, scrollAreaRef]
  );
};
